<template>
    <div class="rate-comparison">
        <!-- 헤더 -->
        <div class="content-header">
            <h3>
                <i class="fas fa-balance-scale"></i>
                요금제 비교
            </h3>
            <!-- 비교 실행 폼 -->
            <div class="verify-form-header">
                <div class="form-field-inline">
                    <label>계약 선택 <span class="required">*</span></label>
                    <select
                        v-model.number="form.contractId"
                        class="form-select-header contract-select-wide"
                        @change="onContractChange"
                    >
                        <option :value="null">선택하세요</option>
                        <option
                            v-for="contract in contractList"
                            :key="contract.contractIdx"
                            :value="contract.contractIdx"
                        >
                            {{ getContractDisplayText(contract) }}
                        </option>
                    </select>
                </div>

                <div class="form-field-inline">
                    <label>비교할 요금제 <span class="required">*</span></label>
                    <select v-model.number="form.comparedCostIdx" class="form-select-header">
                        <option :value="null">선택하세요</option>
                        <option v-for="cost in filteredCostList" :key="cost.costIdx" :value="cost.costIdx">
                            {{ cost.costName }}
                        </option>
                    </select>
                </div>

                <div class="form-field-inline">
                    <label>집계년월</label>
                    <div
                        class="date-picker-container-header"
                        v-click-outside="closeDatePicker"
                        @click="toggleDatePicker"
                    >
                        <div class="date-display-header">
                            <span>{{ dateDisplay || "최근 계산 내역" }}</span>
                            <i :class="['fas fa-chevron-down', { rotate: showDatePicker }]"></i>
                        </div>

                        <!-- 드롭다운 -->
                        <div v-if="showDatePicker" class="date-dropdown">
                            <div class="year-selector">
                                <button @click.stop="changeYear(-1)">
                                    <i class="fas fa-chevron-left"></i>
                                </button>
                                <span>{{ dateYear }}</span>
                                <button @click.stop="changeYear(1)">
                                    <i class="fas fa-chevron-right"></i>
                                </button>
                            </div>
                            <div class="month-grid">
                                <button
                                    v-for="month in 12"
                                    :key="month"
                                    :class="['month-btn', { active: month === dateMonth }]"
                                    @click.stop="selectMonth(month)"
                                >
                                    {{ month }}월
                                </button>
                            </div>
                            <div class="date-clear-btn">
                                <button @click.stop="clearDate" class="btn-clear-date">초기화 (최근 계산 내역)</button>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="form-field-inline">
                    <label>비교할 요금제 계약용량</label>
                    <input
                        type="number"
                        v-model.number="form.comparedContractCapacity"
                        class="form-input-header"
                        min="0"
                        :placeholder="
                            selectedContract?.contractCapacity ? `기본값: ${selectedContract.contractCapacity}` : ''
                        "
                    />
                </div>

                <div class="form-field-inline">
                    <button class="btn-verify-header" @click="executeComparison" :disabled="comparing">
                        <i :class="['fas', comparing ? 'fa-spinner fa-spin' : 'fa-balance-scale']"></i>
                        {{ comparing ? "비교 중..." : "비교 실행" }}
                    </button>
                </div>
            </div>
        </div>

        <!-- 2컬럼 레이아웃: 왼쪽(요약) + 오른쪽(상세) -->
        <div class="verification-layout">
            <!-- 왼쪽 영역: 입력 폼 및 요약 -->
            <div class="left-panel">
                <!-- 통합 비교 정보 -->
                <div v-if="comparisonData" class="info-section-compact">
                    <h6 class="section-title-compact">요금제 비교 결과</h6>
                    <table class="comparison-summary-table">
                        <thead>
                            <tr>
                                <th>항목</th>
                                <th>
                                    <div class="rate-plan-header">
                                        <span class="current-badge">현재</span>
                                        <span class="rate-plan-header-title">{{ comparisonData.cost1.costName }}</span>
                                    </div>
                                </th>
                                <th>
                                    <div class="rate-plan-header">
                                        <span class="compare-badge">비교</span>
                                        <span class="rate-plan-header-title">{{ comparisonData.cost2.costName }}</span>
                                    </div>
                                </th>
                                <th>차이</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td class="info-label-cell">계산년월</td>
                                <td class="info-value-cell">{{ formatYearMonth(comparisonData.regYm) }}</td>
                                <td class="info-value-cell">{{ formatYearMonth(comparisonData.regYm) }}</td>
                                <td class="info-value-cell">-</td>
                            </tr>
                            <tr>
                                <td class="info-label-cell">계약용량</td>
                                <td class="info-value-cell">
                                    {{ comparisonData.contractCapacity1 || comparisonData.contractCapacity }}
                                    {{ comparisonData.rate1Result?.rawUnit || "" }}
                                </td>
                                <td class="info-value-cell">
                                    {{ comparisonData.contractCapacity2 || comparisonData.contractCapacity }}
                                    {{ comparisonData.rate2Result?.rawUnit || "" }}
                                </td>
                                <td class="info-value-cell">
                                    <span
                                        v-if="
                                            (comparisonData.contractCapacity1 || comparisonData.contractCapacity) !==
                                            (comparisonData.contractCapacity2 || comparisonData.contractCapacity)
                                        "
                                    >
                                        {{
                                            formatNumber(
                                                (comparisonData.contractCapacity2 || comparisonData.contractCapacity) -
                                                    (comparisonData.contractCapacity1 ||
                                                        comparisonData.contractCapacity),
                                                0,
                                            )
                                        }}{{ comparisonData.rate1Result?.rawUnit || "" }}
                                    </span>
                                    <span v-else>-</span>
                                </td>
                            </tr>
                            <tr>
                                <td class="info-label-cell">총사용량</td>
                                <td class="info-value-cell">
                                    {{
                                        formatNumber(
                                            comparisonData.rate1Result?.rawUsage ||
                                                comparisonData.rate1Result?.billingUsage ||
                                                0,
                                            2,
                                        )
                                    }}{{ comparisonData.rate1Result?.rawUnit || "" }}
                                </td>
                                <td class="info-value-cell">
                                    {{
                                        formatNumber(
                                            comparisonData.rate2Result?.rawUsage ||
                                                comparisonData.rate2Result?.billingUsage ||
                                                0,
                                            2,
                                        )
                                    }}{{ comparisonData.rate2Result?.rawUnit || "" }}
                                </td>
                                <td class="info-value-cell">-</td>
                            </tr>
                            <tr class="rate-plan-details-row">
                                <td class="info-label-cell">기본 요금</td>
                                <td class="info-value-cell rate-plan-details">
                                    <div class="rate-detail-item">
                                        <span class="rate-detail-label">부과 방식:</span>
                                        <span class="rate-detail-value">{{
                                            getBaseFeeTypeName(comparisonData.rate1Result?.costDetails?.baseFeeCalcType)
                                        }}</span>
                                    </div>
                                    <div class="rate-detail-item">
                                        <span class="rate-detail-label">단가:</span>
                                        <span class="rate-detail-value"
                                            >{{
                                                formatNumber(
                                                    comparisonData.rate1Result?.costDetails?.baseFeeValue || 0,
                                                    2,
                                                )
                                            }}원/{{ comparisonData.rate1Result?.rawUnit || "" }}</span
                                        >
                                    </div>
                                </td>
                                <td class="info-value-cell rate-plan-details">
                                    <div class="rate-detail-item">
                                        <span class="rate-detail-label">부과 방식:</span>
                                        <span class="rate-detail-value">{{
                                            getBaseFeeTypeName(comparisonData.rate2Result?.costDetails?.baseFeeCalcType)
                                        }}</span>
                                    </div>
                                    <div class="rate-detail-item">
                                        <span class="rate-detail-label">단가:</span>
                                        <span class="rate-detail-value"
                                            >{{
                                                formatNumber(
                                                    comparisonData.rate2Result?.costDetails?.baseFeeValue || 0,
                                                    2,
                                                )
                                            }}원/{{ comparisonData.rate2Result?.rawUnit || "" }}</span
                                        >
                                    </div>
                                </td>
                                <td class="info-value-cell">-</td>
                            </tr>
                            <tr class="rate-plan-details-row">
                                <td class="info-label-cell">
                                    사용량 요금
                                    <button
                                        v-if="hasTimeSlotRateDetails"
                                        type="button"
                                        class="btn-tariff-toggle"
                                        @click="showFullTariffRates = !showFullTariffRates"
                                    >
                                        {{ showFullTariffRates ? "적용 계절만" : "전체 요금표" }}
                                    </button>
                                </td>
                                <td class="info-value-cell rate-plan-details">
                                    <template v-if="comparisonData.rate1Result?.costDetails?.hasFlatCost === 1">
                                        <div class="rate-detail-item">
                                            <span class="rate-detail-label">단일요금:</span>
                                            <span class="rate-detail-value"
                                                >{{
                                                    formatNumber(
                                                        comparisonData.rate1Result?.costDetails?.flatCostFee || 0,
                                                        2,
                                                    )
                                                }}원/{{ comparisonData.rate1Result?.billingUnit || "" }}</span
                                            >
                                        </div>
                                    </template>
                                    <template v-else-if="displayUsageFeeDetails1.length > 0">
                                        <div
                                            v-for="(detail, idx) in displayUsageFeeDetails1"
                                            :key="'r1-' + idx"
                                            class="rate-detail-item"
                                            :title="detail.hourHint || ''"
                                        >
                                            <span class="rate-detail-label">{{ getTimeSlotLabel(detail) }}:</span>
                                            <span class="rate-detail-value"
                                                >{{ formatNumber(detail.unitPrice || 0, 2) }}원/{{
                                                    comparisonData.rate1Result?.billingUnit || ""
                                                }}</span
                                            >
                                        </div>
                                    </template>
                                    <template v-else>
                                        <span class="rate-detail-value">-</span>
                                    </template>
                                </td>
                                <td class="info-value-cell rate-plan-details">
                                    <template v-if="comparisonData.rate2Result?.costDetails?.hasFlatCost === 1">
                                        <div class="rate-detail-item">
                                            <span class="rate-detail-label">단일요금:</span>
                                            <span class="rate-detail-value"
                                                >{{
                                                    formatNumber(
                                                        comparisonData.rate2Result?.costDetails?.flatCostFee || 0,
                                                        2,
                                                    )
                                                }}원/{{ comparisonData.rate2Result?.billingUnit || "" }}</span
                                            >
                                        </div>
                                    </template>
                                    <template v-else-if="displayUsageFeeDetails2.length > 0">
                                        <div
                                            v-for="(detail, idx) in displayUsageFeeDetails2"
                                            :key="'r2-' + idx"
                                            class="rate-detail-item"
                                            :title="detail.hourHint || ''"
                                        >
                                            <span class="rate-detail-label">{{ getTimeSlotLabel(detail) }}:</span>
                                            <span class="rate-detail-value"
                                                >{{ formatNumber(detail.unitPrice || 0, 2) }}원/{{
                                                    comparisonData.rate2Result?.billingUnit || ""
                                                }}</span
                                            >
                                        </div>
                                    </template>
                                    <template v-else>
                                        <span class="rate-detail-value">-</span>
                                    </template>
                                </td>
                                <td class="info-value-cell">-</td>
                            </tr>
                            <tr class="fee-total-row">
                                <td class="info-label-cell"><strong>합계</strong></td>
                                <td class="info-value-cell fee-value">
                                    <strong>{{ formatNumber(comparisonData.rate1Result.totalFee, 0) }}원</strong>
                                </td>
                                <td class="info-value-cell fee-value">
                                    <strong>{{ formatNumber(comparisonData.rate2Result.totalFee, 0) }}원</strong>
                                </td>
                                <td class="info-value-cell fee-value" :class="getDifferenceClass()">
                                    <strong>{{ formatNumber(comparisonData.comparison.difference, 0) }}원</strong>
                                    <div class="difference-detail">
                                        <span class="difference-percent">
                                            (<strong
                                                >{{ comparisonData.comparison.difference >= 0 ? "+" : ""
                                                }}{{ comparisonData.comparison.differencePercent.toFixed(2) }}%</strong
                                            >)
                                        </span>
                                        <span v-if="comparisonData.comparison.difference < 0" class="savings-info">
                                            <br /><strong
                                                >월 {{ formatNumber(comparisonData.comparison.savings, 0) }}원
                                                절감</strong
                                            >
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Placeholder: 비교 실행 전 -->
                <div v-else class="placeholder-section">
                    <div class="placeholder-content">
                        <i class="fas fa-balance-scale"></i>
                        <p>요금제를 선택한 후<br />비교 실행 버튼을 눌러주세요.</p>
                    </div>
                </div>
            </div>

            <!-- 오른쪽 영역: 상세 비교 -->
            <div class="right-panel">
                <div v-if="comparisonData" class="verification-result">
                    <!-- 추천 정보 -->
                    <div class="calc-section-compact">
                        <div class="calc-section-header-compact" @click="showRecommendation = !showRecommendation">
                            <div style="display: flex; align-items: center; gap: 10px">
                                <h6 class="section-subtitle-compact">추천 요금제</h6>
                                <span class="recommended-badge-compact">
                                    {{ comparisonData.recommendation.recommendedCostName }}
                                </span>
                            </div>
                            <button class="btn-toggle-compact">
                                <i :class="['fas', showRecommendation ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
                                {{ showRecommendation ? "접기" : "펼치기" }}
                            </button>
                        </div>
                        <div v-if="showRecommendation" class="calc-card-compact recommendation-card-compact">
                            <!-- 추천 이유 -->
                            <div class="recommendation-list-compact">
                                <div
                                    v-for="(reason, idx) in comparisonData.recommendation.reason"
                                    :key="idx"
                                    class="recommendation-item-compact"
                                >
                                    <i class="fas fa-check-circle"></i>
                                    <span v-html="highlightKeywords(reason)"></span>
                                </div>
                            </div>

                            <!-- 장점 -->
                            <div class="recommendation-list-compact">
                                <div
                                    v-for="(advantage, idx) in comparisonData.recommendation.advantages"
                                    :key="idx"
                                    class="recommendation-item-compact"
                                >
                                    <i class="fas fa-thumbs-up"></i>
                                    <span v-html="highlightKeywords(advantage)"></span>
                                </div>
                            </div>

                            <!-- 고려사항 -->
                            <div
                                v-if="comparisonData.recommendation.considerations.length > 0"
                                class="recommendation-list-compact"
                            >
                                <div
                                    v-for="(consideration, idx) in comparisonData.recommendation.considerations"
                                    :key="idx"
                                    class="recommendation-item-compact"
                                >
                                    <i class="fas fa-info-circle"></i>
                                    <span v-html="highlightKeywords(consideration)"></span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 비용 기여도 (기본/사용·부하별) -->
                    <div v-if="feeContributionRows.length" class="calc-section-compact">
                        <div class="calc-section-header-compact" @click="showContribution = !showContribution">
                            <h6 class="section-subtitle-compact">비용 편익 기여도</h6>
                            <button class="btn-toggle-compact">
                                <i :class="['fas', showContribution ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
                                {{ showContribution ? "접기" : "펼치기" }}
                            </button>
                        </div>
                        <div v-if="showContribution" class="calc-card-compact">
                            <p class="insight-hint">비교 요금제 − 현재 요금제 (음수 = 비교 요금제가 유리)</p>
                            <table class="detail-table-compact">
                                <thead>
                                    <tr>
                                        <th>항목</th>
                                        <th class="text-right">차이</th>
                                        <th class="text-right">비중</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="row in feeContributionRows" :key="row.key">
                                        <td>
                                            <span :class="{ 'contrib-indent': row.indent }">{{ row.label }}</span>
                                        </td>
                                        <td class="text-right" :class="getDiffClass(row.diff)">
                                            {{ formatNumber(row.diff, 0) }}원
                                        </td>
                                        <td class="text-right">{{ row.shareText }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- 요금 상세 비교 -->
                    <div class="calc-section-compact">
                        <div class="calc-section-header-compact" @click="showFeeComparison = !showFeeComparison">
                            <h6 class="section-subtitle-compact">요금 상세 비교</h6>
                            <button class="btn-toggle-compact">
                                <i :class="['fas', showFeeComparison ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
                                {{ showFeeComparison ? "접기" : "펼치기" }}
                            </button>
                        </div>
                        <div v-if="showFeeComparison" class="calc-card-compact">
                            <table class="detail-table-compact">
                                <thead>
                                    <tr>
                                        <th>항목</th>
                                        <th class="text-right">현재 사용중인 요금제</th>
                                        <th class="text-right">비교 대상 요금제</th>
                                        <th class="text-right">비용 편익</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr class="rate-plan-name-row">
                                        <td><strong>요금제 명</strong></td>
                                        <td class="text-right">
                                            <strong>{{ comparisonData.cost1.costName }}</strong>
                                        </td>
                                        <td class="text-right">
                                            <strong>{{ comparisonData.cost2.costName }}</strong>
                                        </td>
                                        <td class="text-right">-</td>
                                    </tr>
                                    <tr>
                                        <td>기본요금</td>
                                        <td class="text-right">
                                            {{ formatNumber(comparisonData.rate1Result.baseFee, 0) }}원
                                        </td>
                                        <td class="text-right">
                                            {{ formatNumber(comparisonData.rate2Result.baseFee, 0) }}원
                                        </td>
                                        <td class="text-right" :class="getDiffClass(getBaseFeeDiff())">
                                            {{ formatNumber(getBaseFeeDiff(), 0) }}원
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>사용량요금</td>
                                        <td class="text-right">
                                            {{ formatNumber(comparisonData.rate1Result.usageFee, 0) }}원
                                        </td>
                                        <td class="text-right">
                                            {{ formatNumber(comparisonData.rate2Result.usageFee, 0) }}원
                                        </td>
                                        <td class="text-right" :class="getDiffClass(getUsageFeeDiff())">
                                            {{ formatNumber(getUsageFeeDiff(), 0) }}원
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>기타요금</td>
                                        <td class="text-right">
                                            {{ formatNumber(comparisonData.rate1Result.etcFee, 0) }}원
                                        </td>
                                        <td class="text-right">
                                            {{ formatNumber(comparisonData.rate2Result.etcFee, 0) }}원
                                        </td>
                                        <td class="text-right" :class="getDiffClass(getEtcFeeDiff())">
                                            {{ formatNumber(getEtcFeeDiff(), 0) }}원
                                        </td>
                                    </tr>
                                    <tr class="subtotal-row-compact">
                                        <td><strong>소계</strong></td>
                                        <td class="text-right">
                                            <strong
                                                >{{ formatNumber(comparisonData.rate1Result.subTotal, 0) }}원</strong
                                            >
                                        </td>
                                        <td class="text-right">
                                            <strong
                                                >{{ formatNumber(comparisonData.rate2Result.subTotal, 0) }}원</strong
                                            >
                                        </td>
                                        <td class="text-right" :class="getDiffClass(getSubTotalDiff())">
                                            <strong>{{ formatNumber(getSubTotalDiff(), 0) }}원</strong>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>부가세</td>
                                        <td class="text-right">
                                            {{ formatNumber(comparisonData.rate1Result.vatAmount, 0) }}원
                                        </td>
                                        <td class="text-right">
                                            {{ formatNumber(comparisonData.rate2Result.vatAmount, 0) }}원
                                        </td>
                                        <td class="text-right" :class="getDiffClass(getVatDiff())">
                                            {{ formatNumber(getVatDiff(), 0) }}원
                                        </td>
                                    </tr>
                                    <tr class="fee-total-row-compact">
                                        <td><strong>합계</strong></td>
                                        <td class="text-right">
                                            <strong
                                                >{{ formatNumber(comparisonData.rate1Result.totalFee, 0) }}원</strong
                                            >
                                        </td>
                                        <td class="text-right">
                                            <strong
                                                >{{ formatNumber(comparisonData.rate2Result.totalFee, 0) }}원</strong
                                            >
                                        </td>
                                        <td
                                            class="text-right"
                                            :class="getDiffClass(comparisonData.comparison.difference)"
                                        >
                                            <strong
                                                >{{ formatNumber(comparisonData.comparison.difference, 0) }}원</strong
                                            >
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- 시간대별 사용 패턴 -->
                    <div v-if="hasTimeSlotData()" class="calc-section-compact">
                        <div class="calc-section-header-compact" @click="showUsagePattern = !showUsagePattern">
                            <h6 class="section-subtitle-compact">시간대별 사용 패턴</h6>
                            <button class="btn-toggle-compact">
                                <i :class="['fas', showUsagePattern ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
                                {{ showUsagePattern ? "접기" : "펼치기" }}
                            </button>
                        </div>
                        <div v-if="showUsagePattern" class="calc-card-compact">
                            <!-- 사용 패턴 바 -->
                            <div v-if="hasUsagePatternData()" class="usage-pattern-compact">
                                <div class="pattern-item-compact">
                                    <div class="pattern-label-compact">경부하</div>
                                    <div class="pattern-bar-compact">
                                        <div
                                            class="pattern-bar-fill-compact low"
                                            :style="{ width: comparisonData.usagePattern.lowPercent + '%' }"
                                        ></div>
                                    </div>
                                    <div class="pattern-value-compact">
                                        {{ formatNumber(comparisonData.usagePattern.lowUsage, 0) }}
                                        ({{ comparisonData.usagePattern.lowPercent.toFixed(2) }}%)
                                    </div>
                                </div>
                                <div class="pattern-item-compact">
                                    <div class="pattern-label-compact">중부하</div>
                                    <div class="pattern-bar-compact">
                                        <div
                                            class="pattern-bar-fill-compact mid"
                                            :style="{ width: comparisonData.usagePattern.midPercent + '%' }"
                                        ></div>
                                    </div>
                                    <div class="pattern-value-compact">
                                        {{ formatNumber(comparisonData.usagePattern.midUsage, 0) }}
                                        ({{ comparisonData.usagePattern.midPercent.toFixed(2) }}%)
                                    </div>
                                </div>
                                <div class="pattern-item-compact">
                                    <div class="pattern-label-compact">최대부하</div>
                                    <div class="pattern-bar-compact">
                                        <div
                                            class="pattern-bar-fill-compact peak"
                                            :style="{ width: comparisonData.usagePattern.peakPercent + '%' }"
                                        ></div>
                                    </div>
                                    <div class="pattern-value-compact">
                                        {{ formatNumber(comparisonData.usagePattern.peakUsage, 0) }}
                                        ({{ comparisonData.usagePattern.peakPercent.toFixed(2) }}%)
                                    </div>
                                </div>
                            </div>
                            <div v-else class="no-pattern-message">
                                <i class="fas fa-info-circle"></i>
                                <span>시간대별 구분이 없는 요금제입니다.</span>
                            </div>

                            <!-- 시간대별 요금 비교 -->
                            <table v-if="hasTimeSlotFee()" class="detail-table-compact" style="margin-top: 12px">
                                <thead>
                                    <tr>
                                        <th>시간대</th>
                                        <th class="text-right">요금제 1</th>
                                        <th class="text-right">요금제 2</th>
                                        <th class="text-right">차이</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>경부하</td>
                                        <td class="text-right">
                                            {{ formatNumber(comparisonData.rate1Result.timeSlotFee?.LOW || 0, 0) }}원
                                        </td>
                                        <td class="text-right">
                                            {{ formatNumber(comparisonData.rate2Result.timeSlotFee?.LOW || 0, 0) }}원
                                        </td>
                                        <td class="text-right" :class="getDiffClass(getTimeSlotFeeDiff('LOW'))">
                                            {{ formatNumber(getTimeSlotFeeDiff("LOW"), 0) }}원
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>중부하</td>
                                        <td class="text-right">
                                            {{ formatNumber(comparisonData.rate1Result.timeSlotFee?.MID || 0, 0) }}원
                                        </td>
                                        <td class="text-right">
                                            {{ formatNumber(comparisonData.rate2Result.timeSlotFee?.MID || 0, 0) }}원
                                        </td>
                                        <td class="text-right" :class="getDiffClass(getTimeSlotFeeDiff('MID'))">
                                            {{ formatNumber(getTimeSlotFeeDiff("MID"), 0) }}원
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>최대부하</td>
                                        <td class="text-right">
                                            {{ formatNumber(comparisonData.rate1Result.timeSlotFee?.PEAK || 0, 0) }}원
                                        </td>
                                        <td class="text-right">
                                            {{ formatNumber(comparisonData.rate2Result.timeSlotFee?.PEAK || 0, 0) }}원
                                        </td>
                                        <td class="text-right" :class="getDiffClass(getTimeSlotFeeDiff('PEAK'))">
                                            {{ formatNumber(getTimeSlotFeeDiff("PEAK"), 0) }}원
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- 계절×부하 단가 매트릭스 (평일) -->
                    <div v-if="rateMatrix.hasData" class="calc-section-compact">
                        <div class="calc-section-header-compact" @click="showRateMatrix = !showRateMatrix">
                            <h6 class="section-subtitle-compact">단가 매트릭스 (평일)</h6>
                            <button class="btn-toggle-compact">
                                <i :class="['fas', showRateMatrix ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
                                {{ showRateMatrix ? "접기" : "펼치기" }}
                            </button>
                        </div>
                        <div v-if="showRateMatrix" class="calc-card-compact">
                            <p class="insight-hint">
                                단위: 원/{{ comparisonData.rate1Result?.billingUnit || "kWh" }} · 적용 계절 행 강조
                            </p>
                            <div class="matrix-grid">
                                <div class="matrix-block">
                                    <div class="matrix-title">
                                        <span class="current-badge">현재</span>
                                        {{ comparisonData.cost1.costName }}
                                    </div>
                                    <table class="detail-table-compact matrix-table">
                                        <thead>
                                            <tr>
                                                <th>계절</th>
                                                <th class="text-right">경부하</th>
                                                <th class="text-right">중부하</th>
                                                <th class="text-right">최대부하</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr
                                                v-for="row in rateMatrix.seasons"
                                                :key="'m1-' + row.code"
                                                :class="{ 'matrix-active-row': row.code === activeSeasonCode }"
                                            >
                                                <td>{{ row.label }}</td>
                                                <td class="text-right">{{ formatMatrixPrice(row.rate1.LOW) }}</td>
                                                <td class="text-right">{{ formatMatrixPrice(row.rate1.MID) }}</td>
                                                <td class="text-right">{{ formatMatrixPrice(row.rate1.PEAK) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div class="matrix-block">
                                    <div class="matrix-title">
                                        <span class="compare-badge">비교</span>
                                        {{ comparisonData.cost2.costName }}
                                    </div>
                                    <table class="detail-table-compact matrix-table">
                                        <thead>
                                            <tr>
                                                <th>계절</th>
                                                <th class="text-right">경부하</th>
                                                <th class="text-right">중부하</th>
                                                <th class="text-right">최대부하</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr
                                                v-for="row in rateMatrix.seasons"
                                                :key="'m2-' + row.code"
                                                :class="{ 'matrix-active-row': row.code === activeSeasonCode }"
                                            >
                                                <td>{{ row.label }}</td>
                                                <td class="text-right">{{ formatMatrixPrice(row.rate2.LOW) }}</td>
                                                <td class="text-right">{{ formatMatrixPrice(row.rate2.MID) }}</td>
                                                <td class="text-right">{{ formatMatrixPrice(row.rate2.PEAK) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 요금제 1 상세 -->
                    <div class="calc-section-compact">
                        <div class="calc-section-header-compact" @click="showRate1Detail = !showRate1Detail">
                            <h6 class="section-subtitle-compact">요금제 1 상세</h6>
                            <button class="btn-toggle-compact">
                                <i :class="['fas', showRate1Detail ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
                                {{ showRate1Detail ? "접기" : "펼치기" }}
                            </button>
                        </div>
                        <div v-if="showRate1Detail" class="calc-card-compact">
                            <div class="result-info-section-compact">
                                <div class="info-row-compact">
                                    <span class="info-label-compact">청구 기간:</span>
                                    <span class="info-value-compact">
                                        {{
                                            formatBillingPeriod(
                                                comparisonData.rate1Result.billingStartAt,
                                                comparisonData.rate1Result.billingEndAt,
                                            )
                                        }}
                                    </span>
                                </div>
                                <div class="info-row-compact">
                                    <span class="info-label-compact">요금제:</span>
                                    <span class="info-value-compact">
                                        {{ comparisonData.rate1Result.calcDetails?.costName || "-" }}
                                    </span>
                                </div>
                            </div>
                            <div class="result-grid-compact">
                                <div class="result-item-compact">
                                    <span class="result-label-compact">기본요금</span>
                                    <span class="result-value-compact">
                                        {{ formatNumber(comparisonData.rate1Result.baseFee, 0) }}원
                                    </span>
                                </div>
                                <div class="result-item-compact">
                                    <span class="result-label-compact">사용량요금</span>
                                    <span class="result-value-compact">
                                        {{ formatNumber(comparisonData.rate1Result.usageFee, 0) }}원
                                    </span>
                                </div>
                                <div class="result-item-compact">
                                    <span class="result-label-compact">기타요금</span>
                                    <span class="result-value-compact">
                                        {{ formatNumber(comparisonData.rate1Result.etcFee, 0) }}원
                                    </span>
                                </div>
                                <div class="result-item-compact highlight-compact">
                                    <span class="result-label-compact">소계</span>
                                    <span class="result-value-compact">
                                        {{ formatNumber(comparisonData.rate1Result.subTotal, 0) }}원
                                    </span>
                                </div>
                                <div class="result-item-compact">
                                    <span class="result-label-compact">부가세</span>
                                    <span class="result-value-compact">
                                        {{ formatNumber(comparisonData.rate1Result.vatAmount, 0) }}원
                                    </span>
                                </div>
                                <div class="result-item-compact total-compact">
                                    <span class="result-label-compact">합계</span>
                                    <span class="result-value-compact">
                                        {{ formatNumber(comparisonData.rate1Result.totalFee, 0) }}원
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 요금제 2 상세 -->
                    <div class="calc-section-compact">
                        <div class="calc-section-header-compact" @click="showRate2Detail = !showRate2Detail">
                            <h6 class="section-subtitle-compact">요금제 2 상세</h6>
                            <button class="btn-toggle-compact">
                                <i :class="['fas', showRate2Detail ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
                                {{ showRate2Detail ? "접기" : "펼치기" }}
                            </button>
                        </div>
                        <div v-if="showRate2Detail" class="calc-card-compact">
                            <div class="result-info-section-compact">
                                <div class="info-row-compact">
                                    <span class="info-label-compact">청구 기간:</span>
                                    <span class="info-value-compact">
                                        {{
                                            formatBillingPeriod(
                                                comparisonData.rate2Result.billingStartAt,
                                                comparisonData.rate2Result.billingEndAt,
                                            )
                                        }}
                                    </span>
                                </div>
                                <div class="info-row-compact">
                                    <span class="info-label-compact">요금제:</span>
                                    <span class="info-value-compact">
                                        {{ comparisonData.rate2Result.calcDetails?.costName || "-" }}
                                    </span>
                                </div>
                            </div>
                            <div class="result-grid-compact">
                                <div class="result-item-compact">
                                    <span class="result-label-compact">기본요금</span>
                                    <span class="result-value-compact">
                                        {{ formatNumber(comparisonData.rate2Result.baseFee, 0) }}원
                                    </span>
                                </div>
                                <div class="result-item-compact">
                                    <span class="result-label-compact">사용량요금</span>
                                    <span class="result-value-compact">
                                        {{ formatNumber(comparisonData.rate2Result.usageFee, 0) }}원
                                    </span>
                                </div>
                                <div class="result-item-compact">
                                    <span class="result-label-compact">기타요금</span>
                                    <span class="result-value-compact">
                                        {{ formatNumber(comparisonData.rate2Result.etcFee, 0) }}원
                                    </span>
                                </div>
                                <div class="result-item-compact highlight-compact">
                                    <span class="result-label-compact">소계</span>
                                    <span class="result-value-compact">
                                        {{ formatNumber(comparisonData.rate2Result.subTotal, 0) }}원
                                    </span>
                                </div>
                                <div class="result-item-compact">
                                    <span class="result-label-compact">부가세</span>
                                    <span class="result-value-compact">
                                        {{ formatNumber(comparisonData.rate2Result.vatAmount, 0) }}원
                                    </span>
                                </div>
                                <div class="result-item-compact total-compact">
                                    <span class="result-label-compact">합계</span>
                                    <span class="result-value-compact">
                                        {{ formatNumber(comparisonData.rate2Result.totalFee, 0) }}원
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Placeholder: 비교 실행 전 -->
                <div v-else class="placeholder-section">
                    <div class="placeholder-content">
                        <i class="fas fa-balance-scale"></i>
                        <p>비교 실행 후<br />상세 정보가 표시됩니다.</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import backEndApi from "@src/api/backEndApi.js";

export default {
    name: "RateComparison",
    directives: {
        clickOutside: {
            bind(el, binding) {
                el.clickOutsideEvent = function (event) {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value(event);
                    }
                };
                document.body.addEventListener("click", el.clickOutsideEvent);
            },
            unbind(el) {
                document.body.removeEventListener("click", el.clickOutsideEvent);
            },
        },
    },
    data() {
        return {
            form: {
                contractId: null,
                comparedCostIdx: null,
                regYmMonth: "",
                contractCapacity: null,
                comparedContractCapacity: null,
            },
            comparing: false,
            comparisonData: null,
            pointList: [],
            contractList: [],
            selectedContract: null,
            costList: [],
            showRecommendation: true, // 기본 펼침
            showFeeComparison: true, // 기본 펼침
            showUsageComparison: true, // 기본 펼침
            showUsagePattern: true, // 기본 펼침
            showContribution: true,
            showRateMatrix: true,
            showFullTariffRates: false, // 기본: 비교월 적용 계절만
            showRate1Detail: true, // 기본 펼침
            showRate2Detail: true, // 기본 펼침
            // 날짜 선택기
            showDatePicker: false,
            dateYear: new Date().getFullYear(),
            dateMonth: new Date().getMonth() + 1,
        };
    },
    computed: {
        dateDisplay() {
            if (this.form.regYmMonth) {
                const [year, month] = this.form.regYmMonth.split("-");
                return `${year}년 ${parseInt(month)}월`;
            }
            return null; // 날짜 미선택 시 null 반환하여 "최근 bill 사용" 표시
        },
        // 계약 선택 시 동일 사업자의 요금제만 필터링
        filteredCostList() {
            // 계약이 선택되지 않았으면 모든 요금제 표시
            if (!this.selectedContract || !this.selectedContract.supplier) {
                return this.costList;
            }

            // 선택된 계약의 사업자 ID
            const selectedSupplierIdx = this.selectedContract.supplier.supplierIdx;

            // 동일 사업자의 요금제만 필터링
            return this.costList.filter((cost) => {
                // cost에 supplierIdx가 직접 있거나 supplier 객체가 있는 경우
                const costSupplierIdx = cost.supplierIdx || (cost.supplier && cost.supplier.supplierIdx);
                return costSupplierIdx === selectedSupplierIdx;
            });
        },
        activeSeasonCode() {
            const ym = this.comparisonData?.regYm;
            if (!ym || String(ym).length < 6) return null;
            const month = parseInt(String(ym).substring(4, 6), 10);
            if (month >= 6 && month <= 8) return "SUMMER";
            if (month >= 11 || month <= 2) return "WINTER";
            return "MID";
        },
        hasTimeSlotRateDetails() {
            const d1 = this.comparisonData?.rate1Result?.costDetails?.usageFeeDetails || [];
            const d2 = this.comparisonData?.rate2Result?.costDetails?.usageFeeDetails || [];
            return d1.some((d) => d.type !== "flat") || d2.some((d) => d.type !== "flat");
        },
        displayUsageFeeDetails1() {
            return this.buildDisplayUsageFeeDetails(this.comparisonData?.rate1Result?.costDetails?.usageFeeDetails);
        },
        displayUsageFeeDetails2() {
            return this.buildDisplayUsageFeeDetails(this.comparisonData?.rate2Result?.costDetails?.usageFeeDetails);
        },
        feeContributionRows() {
            if (!this.comparisonData?.rate1Result || !this.comparisonData?.rate2Result) return [];
            const r1 = this.comparisonData.rate1Result;
            const r2 = this.comparisonData.rate2Result;
            const bd = this.comparisonData.recommendation?.breakdown;
            const baseDiff = bd ? bd.baseFeeDiff : (r2.baseFee || 0) - (r1.baseFee || 0);
            const usageDiff = bd ? bd.usageFeeDiff : (r2.usageFee || 0) - (r1.usageFee || 0);
            const etcDiff = bd ? bd.etcFeeDiff : (r2.etcFee || 0) - (r1.etcFee || 0);
            const vatDiff = bd ? bd.vatAmountDiff : (r2.vatAmount || 0) - (r1.vatAmount || 0);
            const totalDiff = this.comparisonData.comparison?.difference ?? baseDiff + usageDiff + etcDiff + vatDiff;
            const absTotal = Math.abs(totalDiff) || 1;
            const slotDiff = bd?.timeSlotFeeDiff || {
                LOW: this.getTimeSlotFeeDiff("LOW"),
                MID: this.getTimeSlotFeeDiff("MID"),
                PEAK: this.getTimeSlotFeeDiff("PEAK"),
            };
            const share = (v) => `${((Math.abs(v) / absTotal) * 100).toFixed(1)}%`;
            const rows = [
                { key: "base", label: "기본요금", diff: baseDiff, shareText: share(baseDiff) },
                { key: "usage", label: "사용량요금", diff: usageDiff, shareText: share(usageDiff) },
                {
                    key: "low",
                    label: "↳ 경부하",
                    diff: slotDiff.LOW || 0,
                    shareText: share(slotDiff.LOW || 0),
                    indent: true,
                },
                {
                    key: "mid",
                    label: "↳ 중부하",
                    diff: slotDiff.MID || 0,
                    shareText: share(slotDiff.MID || 0),
                    indent: true,
                },
                {
                    key: "peak",
                    label: "↳ 최대부하",
                    diff: slotDiff.PEAK || 0,
                    shareText: share(slotDiff.PEAK || 0),
                    indent: true,
                },
                { key: "etc", label: "기타요금", diff: etcDiff, shareText: share(etcDiff) },
                { key: "vat", label: "부가세", diff: vatDiff, shareText: share(vatDiff) },
                { key: "total", label: "합계", diff: totalDiff, shareText: "100%" },
            ];
            return rows;
        },
        rateMatrix() {
            const empty = { hasData: false, seasons: [] };
            if (!this.comparisonData) return empty;
            const d1 = this.comparisonData.rate1Result?.costDetails?.usageFeeDetails || [];
            const d2 = this.comparisonData.rate2Result?.costDetails?.usageFeeDetails || [];
            if (!d1.length && !d2.length) return empty;
            if (d1.some((d) => d.type === "flat") || d2.some((d) => d.type === "flat")) return empty;

            const pick = (details, season, slot) => {
                const hit = details.find(
                    (d) => d.seasonCode === season && d.dayType === "WEEKDAY" && d.timeSlotCode === slot,
                );
                return hit ? parseFloat(hit.unitPrice) : null;
            };
            const seasons = ["SUMMER", "MID", "WINTER"].map((code) => ({
                code,
                label: this.getSeasonLabel(code),
                rate1: {
                    LOW: pick(d1, code, "LOW"),
                    MID: pick(d1, code, "MID"),
                    PEAK: pick(d1, code, "PEAK"),
                },
                rate2: {
                    LOW: pick(d2, code, "LOW"),
                    MID: pick(d2, code, "MID"),
                    PEAK: pick(d2, code, "PEAK"),
                },
            }));
            const hasData = seasons.some(
                (s) =>
                    s.rate1.LOW != null ||
                    s.rate1.MID != null ||
                    s.rate1.PEAK != null ||
                    s.rate2.LOW != null ||
                    s.rate2.MID != null ||
                    s.rate2.PEAK != null,
            );
            return { hasData, seasons };
        },
    },
    mounted() {
        this.loadContracts();
        this.loadCosts();
    },
    // 설정 모달 keep-alive: 다른 탭에서 바뀐 목록만 다시 받고 입력·결과는 유지
    activated() {
        if (!this.wasDeactivated) return;
        this.loadContracts();
        this.loadCosts();
    },
    deactivated() {
        this.wasDeactivated = true;
    },
    methods: {
        async loadContracts() {
            try {
                const result = await backEndApi.energyBillCalc.getContractList();
                if (result.status === 200) {
                    // 활성 계약만 필터링
                    this.contractList = result.data.filter((contract) => contract.enabled === "Y");
                }
            } catch (error) {
                console.error("계약 목록 조회 실패:", error);
            }
        },

        async loadCosts() {
            try {
                const result = await backEndApi.energyBillCalc.getCostList({
                    supplierIdx: null,
                    costType: "",
                    enabled: "",
                });
                if (result.status === 200) {
                    this.costList = result.data;
                }
            } catch (error) {
                console.error("요금제 목록 조회 실패:", error);
            }
        },

        onContractChange() {
            // 계약 선택 시 해당 계약 정보 조회
            if (this.form.contractId) {
                this.selectedContract = this.contractList.find((c) => c.contractIdx === this.form.contractId);
                // 비교할 요금제의 계약용량 초기값 설정 (선택한 계약의 계약용량으로 항상 초기화)
                if (this.selectedContract && this.selectedContract.contractCapacity) {
                    // 계약 선택 시 항상 계약의 계약용량으로 초기화
                    this.form.comparedContractCapacity = this.selectedContract.contractCapacity;
                    // 하위 호환성을 위해 contractCapacity도 설정
                    this.form.contractCapacity = this.selectedContract.contractCapacity;
                }
                // 집계년월 기본값: 전월 (예측/미완료 월 회피)
                this.form.regYmMonth = this.getDefaultRegYmMonth();
                const [year, month] = this.form.regYmMonth.split("-");
                this.dateYear = parseInt(year);
                this.dateMonth = parseInt(month);
                // 계약 변경 시 비교할 요금제 선택 초기화 (다른 사업자 요금제가 선택되어 있을 수 있음)
                this.form.comparedCostIdx = null;
            } else {
                this.selectedContract = null;
                this.form.contractCapacity = null;
                this.form.comparedContractCapacity = null;
                this.form.comparedCostIdx = null;
                this.form.regYmMonth = "";
            }
        },
        // 날짜 선택기 메서드
        toggleDatePicker() {
            this.showDatePicker = !this.showDatePicker;
            if (this.showDatePicker && this.form.regYmMonth) {
                const [year, month] = this.form.regYmMonth.split("-");
                this.dateYear = parseInt(year);
                this.dateMonth = parseInt(month);
            }
        },
        closeDatePicker() {
            this.showDatePicker = false;
        },
        changeYear(direction) {
            this.dateYear += direction;
            this.updateDate();
        },
        selectMonth(month) {
            this.dateMonth = month;
            this.showDatePicker = false;
            this.updateDate();
        },
        updateDate() {
            const year = String(this.dateYear);
            const month = String(this.dateMonth).padStart(2, "0");
            this.form.regYmMonth = `${year}-${month}`;
        },

        clearDate() {
            // 날짜 초기화 (최근 bill 사용)
            this.form.regYmMonth = "";
            this.showDatePicker = false;
        },

        getDefaultRegYmMonth() {
            const now = new Date();
            const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const year = String(d.getFullYear());
            const month = String(d.getMonth() + 1).padStart(2, "0");
            return `${year}-${month}`;
        },

        convertToYYYYMM(ymMonth) {
            if (!ymMonth) return "";
            return ymMonth.replace("-", "");
        },

        async executeComparison() {
            if (!this.form.contractId) {
                await this.alertWarning("계약을 선택해주세요.");
                return;
            }

            if (!this.form.comparedCostIdx) {
                await this.alertWarning("비교할 요금제를 선택해주세요.");
                return;
            }

            this.comparing = true;
            this.comparisonData = null;
            this.showFullTariffRates = false;

            try {
                const params = {
                    contractId: this.form.contractId,
                    comparedCostIdx: this.form.comparedCostIdx,
                };

                // 선택적 파라미터 추가
                if (this.form.regYmMonth) {
                    params.regYm = this.convertToYYYYMM(this.form.regYmMonth);
                }

                // 비교할 요금제(comparedCostIdx)에만 적용되는 계약용량
                // 기존 요금제(contractId의 계약)는 계약의 실제 contractCapacity를 사용
                // 백엔드 API: contractCapacity 파라미터는 비교할 요금제(comparedCostIdx)에만 적용됨
                //            기존 요금제(contractId)는 계약에 저장된 실제 contractCapacity를 사용
                if (this.form.comparedContractCapacity !== null && this.form.comparedContractCapacity !== undefined) {
                    params.contractCapacity = this.form.comparedContractCapacity;
                } else if (this.selectedContract?.contractCapacity) {
                    // 입력값이 없으면 현재 계약의 계약용량을 기본값으로 사용
                    params.contractCapacity = this.selectedContract.contractCapacity;
                }

                const result = await backEndApi.energyBillCalc.compareSelectiveRates(params);

                if (result.status === 200) {
                    this.comparisonData = result.data;
                    this.$bvToast.toast("요금제 비교가 완료되었습니다.", {
                        title: "완료",
                        variant: "info",
                        solid: true,
                    });
                } else {
                    await this.alertWarning(result.data?.detail || result.data?.message || "비교에 실패했습니다.");
                }
            } catch (error) {
                console.error("요금제 비교 실패:", error);
                const detail = error?.response?.data?.detail || error?.response?.data?.message || error?.message;
                await this.alertWarning(detail || "요금제 비교 중 오류가 발생했습니다.");
            } finally {
                this.comparing = false;
            }
        },

        formatYearMonth(ym) {
            if (!ym) return "-";
            return `${ym.substring(0, 4)}년 ${ym.substring(4, 6)}월`;
        },

        getBaseFeeTypeName(calcType) {
            const typeMap = {
                Fixed: "정액",
                Contract: "계약 전력",
                Demand: "최대 수요",
                Progressive: "누진제",
                PeakLinked: "피크연동제",
                None: "없음",
            };
            return typeMap[calcType] || calcType || "-";
        },

        getSeasonLabel(code) {
            const seasonMap = {
                SUMMER: "하계",
                WINTER: "동계",
                MID: "봄/가을",
                SPRING: "춘계",
                FALL: "추계",
            };
            return seasonMap[code] || code || "";
        },

        getTimeSlotName(code) {
            const timeSlotMap = { PEAK: "최대부하", MID: "중부하", LOW: "경부하", OFF_PEAK: "경부하" };
            return timeSlotMap[code] || code || "";
        },

        getDayTypeLabel(code) {
            const dayTypeMap = { WEEKDAY: "평일", WEEKEND: "주말", HOLIDAY: "공휴일" };
            return dayTypeMap[code] || code || "";
        },

        getTimeSlotLabel(detail) {
            if (detail.type === "flat") {
                return "단일요금";
            }
            const parts = [];
            if (detail.seasonCode) parts.push(this.getSeasonLabel(detail.seasonCode));
            if (detail.timeSlotCode) parts.push(this.getTimeSlotName(detail.timeSlotCode));
            if (detail.dayType) parts.push(this.getDayTypeLabel(detail.dayType));
            return parts.length > 0 ? parts.join(" ") : "기본";
        },

        /** season×dayType×timeSlot 유니크 단가 + (옵션) 적용 계절 필터 */
        buildDisplayUsageFeeDetails(rawDetails) {
            if (!rawDetails || !rawDetails.length) return [];
            if (rawDetails.some((d) => d.type === "flat")) return rawDetails;

            const seasonOrder = { SUMMER: 0, MID: 1, WINTER: 2 };
            const dayOrder = { WEEKDAY: 0, WEEKEND: 1, HOLIDAY: 2 };
            const slotOrder = { LOW: 0, MID: 1, PEAK: 2 };

            const map = new Map();
            for (const d of rawDetails) {
                if (d.unitPrice == null) continue;
                if (
                    !this.showFullTariffRates &&
                    this.activeSeasonCode &&
                    d.seasonCode &&
                    d.seasonCode !== this.activeSeasonCode
                ) {
                    continue;
                }
                const key = `${d.seasonCode || ""}|${d.dayType || ""}|${d.timeSlotCode || ""}`;
                const hourBit = d.hourStart != null && d.hourEnd != null ? `${d.hourStart}-${d.hourEnd}` : null;
                if (!map.has(key)) {
                    map.set(key, {
                        ...d,
                        unitPrice: parseFloat(d.unitPrice),
                        _hours: hourBit ? [hourBit] : [],
                    });
                } else if (hourBit) {
                    map.get(key)._hours.push(hourBit);
                }
            }

            return [...map.values()]
                .map((d) => ({
                    ...d,
                    hourHint: d._hours.length ? `시간대: ${d._hours.join(", ")}` : "",
                }))
                .sort((a, b) => {
                    const s = (seasonOrder[a.seasonCode] ?? 9) - (seasonOrder[b.seasonCode] ?? 9);
                    if (s) return s;
                    const dy = (dayOrder[a.dayType] ?? 9) - (dayOrder[b.dayType] ?? 9);
                    if (dy) return dy;
                    return (slotOrder[a.timeSlotCode] ?? 9) - (slotOrder[b.timeSlotCode] ?? 9);
                });
        },

        formatMatrixPrice(v) {
            if (v == null || Number.isNaN(v)) return "-";
            return this.formatNumber(v, 1);
        },

        formatBillingPeriod(startAt, endAt) {
            const formatDate = (dateStr) => {
                if (!dateStr) return "-";
                if (dateStr.includes("-")) return dateStr;
                if (dateStr.length === 8) {
                    return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
                }
                return dateStr;
            };
            return `${formatDate(startAt)} ~ ${formatDate(endAt)}`;
        },

        // 날짜 포맷팅 헬퍼 메서드
        formatDate(dateStr) {
            if (!dateStr) return "-";
            // 이미 YYYY-MM-DD 형식인 경우 그대로 반환
            if (dateStr.includes("-")) return dateStr;
            // YYYYMMDD 형식인 경우 변환
            if (dateStr.length === 8) {
                return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
            }
            return dateStr;
        },

        // 계약 선택 드롭다운 표시 텍스트 생성
        getContractDisplayText(contract) {
            if (!contract) return "-";

            const contractName = contract.contractName || "-";
            const costName = contract.cost?.costName || "-";

            // 유효기간 포맷팅
            let periodText = "";
            if (contract.startDate) {
                const startDate = this.formatDate(contract.startDate);
                if (contract.endDate) {
                    const endDate = this.formatDate(contract.endDate);
                    periodText = ` [${startDate} ~ ${endDate}]`;
                } else {
                    periodText = ` [${startDate} ~ 진행중]`;
                }
            }

            return `${contractName} (${costName})${periodText}`;
        },

        formatNumber(value, decimals = 2) {
            return new Intl.NumberFormat("ko-KR", {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
            }).format(value || 0);
        },

        getDifferenceClass() {
            if (!this.comparisonData) return "";
            return this.comparisonData.comparison.difference < 0 ? "diff-negative" : "diff-positive";
        },

        getDifferenceRowClass() {
            if (!this.comparisonData) return "";
            return this.comparisonData.comparison.difference < 0 ? "difference-negative" : "difference-positive";
        },

        getDiffClass(diff) {
            if (diff < 0) return "diff-negative";
            if (diff > 0) return "diff-positive";
            return "";
        },

        getBaseFeeDiff() {
            if (!this.comparisonData) return 0;
            // 비교 대상 요금제 - 현재 사용중인 요금제
            return this.comparisonData.rate2Result.baseFee - this.comparisonData.rate1Result.baseFee;
        },

        getUsageFeeDiff() {
            if (!this.comparisonData) return 0;
            // 비교 대상 요금제 - 현재 사용중인 요금제
            return this.comparisonData.rate2Result.usageFee - this.comparisonData.rate1Result.usageFee;
        },

        getEtcFeeDiff() {
            if (!this.comparisonData) return 0;
            // 비교 대상 요금제 - 현재 사용중인 요금제
            return this.comparisonData.rate2Result.etcFee - this.comparisonData.rate1Result.etcFee;
        },

        getSubTotalDiff() {
            if (!this.comparisonData) return 0;
            // 비교 대상 요금제 - 현재 사용중인 요금제
            return this.comparisonData.rate2Result.subTotal - this.comparisonData.rate1Result.subTotal;
        },

        getVatDiff() {
            if (!this.comparisonData) return 0;
            // 비교 대상 요금제 - 현재 사용중인 요금제
            return this.comparisonData.rate2Result.vatAmount - this.comparisonData.rate1Result.vatAmount;
        },

        getTimeSlotFeeDiff(slot) {
            if (!this.comparisonData) return 0;
            const fee1 = this.comparisonData.rate1Result.timeSlotFee?.[slot] || 0;
            const fee2 = this.comparisonData.rate2Result.timeSlotFee?.[slot] || 0;
            // 비교 대상 요금제 - 현재 사용중인 요금제
            return fee2 - fee1;
        },

        hasMaxDemand() {
            if (!this.comparisonData) return false;
            return (
                (this.comparisonData.rate1Result.maxDemand && this.comparisonData.rate1Result.maxDemand > 0) ||
                (this.comparisonData.rate2Result.maxDemand && this.comparisonData.rate2Result.maxDemand > 0)
            );
        },

        hasTimeSlotData() {
            if (!this.comparisonData) return false;
            return (
                this.hasTimeSlotFee() ||
                this.hasUsagePatternData() ||
                this.comparisonData.rate1Result.timeSlotUsage !== null ||
                this.comparisonData.rate2Result.timeSlotUsage !== null
            );
        },

        hasTimeSlotFee() {
            if (!this.comparisonData) return false;
            return (
                this.comparisonData.rate1Result.timeSlotFee !== null &&
                this.comparisonData.rate2Result.timeSlotFee !== null
            );
        },

        hasUsagePatternData() {
            if (!this.comparisonData || !this.comparisonData.usagePattern) return false;
            const pattern = this.comparisonData.usagePattern;
            return pattern.lowUsage > 0 || pattern.midUsage > 0 || pattern.peakUsage > 0;
        },

        // 주요 키워드를 BOLD로 강조하는 메서드
        highlightKeywords(text) {
            if (!text) return "";
            // 주요 키워드 패턴: 절감, 절약, 비율, %, 원, 월, 연, 저장, 추천, 유리, 낮은, 높은, 단가, 요금제 등
            const keywords = [
                /(\d+\.?\d*%)/g, // 비율 (예: 0.66%)
                /([\d,]+원)/g, // 금액 (예: 482,040원, 5,863,116원) - 천단위 구분자 포함 전체 숫자 매칭
                /(월\s*[\d,]+원)/g, // 월 절감 금액
                /(연\s*[\d,]+원)/g, // 연 절감 금액
                /(절감|절약|저장|추천|유리|낮은|높은|단가|요금제|비용|요금|절감율|절약율)/g,
            ];

            // v-html 로 넣으므로 서버 문자열은 먼저 이스케이프한다
            let highlighted = String(text)
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;");
            // 이미 강조된 구간(<strong>…</strong>) 안은 다시 감싸지 않는다
            keywords.forEach((pattern) => {
                highlighted = highlighted
                    .split(/(<strong>.*?<\/strong>)/)
                    .map((part) => (part.startsWith("<strong>") ? part : part.replace(pattern, "<strong>$1</strong>")))
                    .join("");
            });

            return highlighted;
        },
    },
};
</script>

<style scoped>
.rate-comparison {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* 헤더 */
.content-header {
    margin-bottom: 20px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--ebc-hairline);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
}

.content-header h3 {
    font-size: 15px;
    font-weight: 700;
    color: var(--ebc-text);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.content-header h3 i {
    color: var(--ebc-primary);
}

/* 헤더 안의 검증 폼 */
.verify-form-header {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
    justify-content: flex-end;
}

.verify-form-header .form-field-inline {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.verify-form-header .form-field-inline:first-child {
    flex: 1 1 160px;
}

.verify-form-header .form-field-inline:last-child {
    flex-shrink: 0;
}

.verify-form-header .form-field-inline label {
    font-size: 12px;
    font-weight: 600;
    color: var(--ebc-text);
    white-space: nowrap;
    margin: 0;
}

.form-select-header {
    padding: 6px 10px;
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    font-size: 12px;
    transition: all 0.2s;
    min-width: 96px;
    height: 32px;
}

.form-select-header.contract-select-wide {
    min-width: 0;
    width: 100%;
    max-width: 400px;
}

.form-select-header:focus {
    outline: none;
    border-color: var(--ebc-primary);
    box-shadow: 0 0 0 2px var(--ebc-primary-tint);
}

.form-input-header {
    padding: 6px 10px;
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    font-size: 12px;
    transition: all 0.2s;
    min-width: 72px;
    width: 96px;
    height: 32px;
}

.form-input-header:focus {
    outline: none;
    border-color: var(--ebc-primary);
    box-shadow: 0 0 0 2px var(--ebc-primary-tint);
}

.date-picker-container-header {
    position: relative;
    cursor: pointer;
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    z-index: 100;
    min-width: 140px;
    height: 32px;
}

.date-display-header {
    padding: 6px 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    min-width: 140px;
    height: 100%;
    box-sizing: border-box;
    border-radius: var(--ebc-radius-sm);
    transition: all 0.2s ease;
    color: var(--ebc-text);
    font-weight: 600;
    font-size: 12px;
}

.date-display-header:hover {
    background: var(--ebc-track);
}

.date-display-header i {
    font-size: 10px;
    transition: transform 0.2s ease;
}

.date-display-header i.rotate {
    transform: rotate(180deg);
}

.btn-verify-header {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: none;
    border-radius: var(--ebc-radius-xs);
    background: var(--ebc-primary);
    color: white;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    flex-shrink: 0;
}

.btn-verify-header:hover:not(:disabled) {
    background: var(--ebc-primary-hover);
}

.btn-verify-header:disabled {
    background: var(--ebc-border);
    color: var(--ebc-text-muted);
    cursor: not-allowed;
    transform: none;
}

/* 2컬럼 레이아웃 - BillVerification 스타일 재사용 */
.verification-layout {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    align-items: start;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    height: 100%;
}

/* 왼쪽 패널 */
.left-panel {
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: sticky;
    top: 0;
    align-self: start;
    height: 100%;
    overflow-y: auto;
}

/* 오른쪽 패널 */
.right-panel {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-height: 0;
    overflow: hidden;
    height: 100%;
}

.verification-result {
    display: flex;
    flex-direction: column;
    gap: 16px;
    overflow-y: auto;
    overflow-x: hidden;
    height: 100%;
    padding-right: 8px;
}

/* 입력 폼 - BillVerification 스타일 재사용 */
.verify-form-compact {
    /* background: white;
    border: 1px solid var(--ebc-hairline);
    border-radius: var(--ebc-radius-md);
    padding: 20px; */
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 10px;
    gap: 10px;
    align-items: flex-end;
}

.verify-form-compact .form-field {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 200px;
}

.verify-form-compact .form-field:last-child {
    flex: 0 0 auto;
}

/* 모든 필드의 입력 요소 높이를 동일하게 맞춤 (설명 텍스트 제외) */
.verify-form-compact .form-field .form-input,
.verify-form-compact .form-field .form-select,
.verify-form-compact .form-field .date-picker-container {
    height: 38px;
    box-sizing: border-box;
}

/* 설명 텍스트는 입력 필드 아래에 배치되므로 정렬에 영향 없음 */
.verify-form-compact .form-field .form-text {
    margin-top: 4px;
    font-size: 11px;
    color: var(--ebc-text-sub);
    line-height: 1.4;
    height: auto;
}

.verify-form-compact .form-field label {
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 600;
    color: var(--ebc-text);
}

.required {
    color: var(--ebc-danger);
}

.form-input,
.form-select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    font-size: 13px;
    transition: all 0.2s;
}

.form-input:focus,
.form-select:focus {
    outline: none;
    border-color: var(--ebc-primary);
    box-shadow: 0 0 0 3px var(--ebc-primary-tint);
}

.btn-verify {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-lg);
    padding: 0 20px;
    font-size: 13px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: none;
    border-radius: var(--ebc-radius-xs);
    background: var(--ebc-primary);
    color: white;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    width: 100%;
}

.btn-verify:hover:not(:disabled) {
    background: var(--ebc-primary-hover);
}

.btn-verify:disabled {
    background: var(--ebc-border);
    color: var(--ebc-text-muted);
    cursor: not-allowed;
    transform: none;
}

/* 기본 정보 - BillVerification 스타일 재사용 */
.info-section-compact {
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-md);
    padding: 16px;
    margin-bottom: 12px;
}

.section-title-compact {
    margin: 0 0 16px 0;
    font-size: 14px;
    font-weight: 700;
    color: var(--ebc-text);
    padding-bottom: 8px;
    border-bottom: 1px solid var(--ebc-hairline);
}

/* 통합 비교 정보 테이블 */
.comparison-summary-table {
    width: 100%;
    border-collapse: collapse;
    border: 1px solid var(--ebc-border);
}

.comparison-summary-table thead {
    background: var(--ebc-surface-sunken);
}

.comparison-summary-table th {
    padding: 14px 12px;
    text-align: left;
    font-size: 13px;
    font-weight: 700;
    color: var(--ebc-text);
    border-bottom: 1px solid var(--ebc-hairline);
    border-right: 1px solid var(--ebc-border);
    vertical-align: middle;
}

.comparison-summary-table th:last-child {
    border-right: none;
}

.comparison-summary-table th:first-child {
    width: 120px;
    min-width: 120px;
    max-width: 120px;
}

.comparison-summary-table th:nth-child(2),
.comparison-summary-table th:nth-child(3) {
    width: 35%;
}

.comparison-summary-table th:last-child {
    width: 20%;
}

.rate-plan-header {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    line-height: 1.5;
}

.rate-plan-header-title {
    font-weight: 600;
    font-size: 12px;
}

.comparison-summary-table td {
    padding: 12px;
    font-size: 13px;
    border-bottom: 1px solid var(--ebc-border);
    border-right: 1px solid var(--ebc-border);
    vertical-align: middle;
}

.comparison-summary-table td:last-child {
    border-right: none;
}

.comparison-summary-table tbody tr:last-child td {
    border-bottom: none;
}

.info-label-cell {
    font-weight: 600;
    color: var(--ebc-text-sub);
    background: var(--ebc-surface-sunken);
    text-align: left;
    word-break: keep-all;
    word-wrap: break-word;
    white-space: normal;
    line-height: 1.5;
}

.info-value-cell {
    color: var(--ebc-text);
    text-align: left;
}

.comparison-summary-table .info-value-cell.fee-value {
    text-align: right;
}

.rate-plan-name {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
}

.current-badge,
.compare-badge {
    display: inline-block;
    padding: 4px 8px;
    background: var(--ebc-track);
    color: var(--ebc-text);
    border-radius: var(--ebc-radius-xs);
    font-size: 11px;
    font-weight: 700;
}

.fee-total-row {
    background: var(--ebc-surface-sunken);
    border-top: 2px solid var(--ebc-border);
}

.fee-total-row td {
    padding-top: 16px;
    padding-bottom: 16px;
}

.fee-value {
    font-size: 14px;
    text-align: right;
}

.fee-value strong {
    font-size: 15px;
}

.difference-detail {
    margin-top: 4px;
    font-size: 16px;
    font-weight: normal;
}

.difference-percent {
    font-weight: 700;
    font-size: 15px;
}

.difference-percent strong {
    font-weight: 700;
    font-size: 15px;
}

.savings-info {
    display: block;
    margin-top: 6px;
    font-weight: 700;
    font-size: 15px;
    color: var(--ebc-positive);
}

.savings-info strong {
    font-weight: 700;
    font-size: 15px;
    color: var(--ebc-positive);
}

.rate-plan-details-row {
    background: var(--ebc-surface-sunken);
}

.rate-plan-details {
    font-size: 12px;
    line-height: 1.6;
    text-align: right;
    padding-right: 16px;
}

.rate-detail-item {
    display: flex;
    gap: 8px;
    margin-bottom: 4px;
    justify-content: flex-end;
}

.rate-detail-item:last-child {
    margin-bottom: 0;
}

.rate-detail-label {
    color: var(--ebc-text-sub);
    font-weight: 500;
    min-width: 70px;
    text-align: left;
}

.rate-detail-value {
    color: var(--ebc-text);
    font-weight: 600;
    text-align: right;
}

.rate-subhint {
    margin-top: 4px;
    font-size: 11px;
    font-weight: 600;
    color: var(--ebc-primary);
}

.btn-tariff-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: var(--ebc-btn-h-xs);
    padding: 0 8px;
    font-size: 11px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    margin-top: 6px;
    border: 1px solid var(--ebc-border-strong);
    border-radius: var(--ebc-radius-xs);
    background: var(--ebc-surface);
    color: var(--ebc-text-sub);
    font-weight: 600;
    cursor: pointer;
}

.btn-tariff-toggle:hover {
    background: var(--ebc-track);
    border-color: var(--ebc-text-muted);
}

.insight-hint {
    margin: 0 0 10px;
    font-size: 12px;
    color: var(--ebc-text-sub);
}

.contrib-indent {
    padding-left: 10px;
    color: var(--ebc-text-sub);
}

.matrix-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.matrix-block {
    min-width: 0;
}

.matrix-title {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    font-size: 12px;
    font-weight: 700;
    color: var(--ebc-text);
}

.matrix-table .matrix-active-row {
    background: var(--ebc-primary-tint);
}

.matrix-table .matrix-active-row td {
    font-weight: 700;
}

@media (max-width: 1100px) {
    .matrix-grid {
        grid-template-columns: 1fr;
    }
}

.info-grid-compact {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
}

.info-row-compact {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 8px;
    background: var(--ebc-surface-sunken);
    border-radius: var(--ebc-radius-xs);
    font-size: 12px;
}

.info-label-compact {
    font-size: 12px;
    font-weight: 600;
    color: var(--ebc-text-sub);
}

.info-value-compact {
    font-size: 12px;
    font-weight: 600;
    color: var(--ebc-text);
    text-align: right;
}

/* 비교 요약 테이블 - BillVerification 스타일 재사용 */
.fee-summary-table-compact {
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    padding: 12px;
    overflow: hidden;
}

.fee-table-compact {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
}

.fee-table-compact tbody tr {
    border-bottom: 1px solid var(--ebc-track);
}

.fee-table-compact tbody tr:last-child {
    border-bottom: none;
}

.fee-label-compact {
    padding: 8px 10px;
    font-weight: 600;
    color: var(--ebc-text-sub);
    font-size: 12px;
}

.fee-value-compact {
    padding: 8px 10px;
    text-align: right;
    font-weight: 700;
    color: var(--ebc-text);
    font-size: 12px;
}

.fee-total-row-compact {
    background: var(--ebc-primary);
}

.fee-total-row-compact.difference-negative {
    background: var(--ebc-positive);
}

.fee-total-row-compact.difference-positive {
    background: var(--ebc-danger);
}

.fee-total-row-compact .fee-label-compact,
.fee-total-row-compact .fee-value-compact {
    color: white;
    font-size: 12px;
}

.fee-total-row-compact td {
    color: white;
    font-size: 12px;
    font-weight: 700;
    padding: 14px 8px;
}

.fee-total-row-compact td strong {
    color: white;
    font-size: 13px;
    font-weight: 700;
}

.difference-percent-compact {
    font-size: 12px;
    opacity: 0.9;
    margin-left: 4px;
}

.savings-info-compact {
    font-size: 12px;
    opacity: 0.9;
    display: block;
    margin-top: 4px;
}

.diff-negative {
    color: var(--ebc-positive);
}

.diff-positive {
    color: var(--ebc-danger);
}

/* 통합 비교 테이블의 차이 셀 스타일 */
.comparison-summary-table .fee-value.diff-negative {
    color: var(--ebc-positive);
}

.comparison-summary-table .fee-value.diff-positive {
    color: var(--ebc-danger);
}

.comparison-summary-table .fee-value.diff-negative .difference-percent,
.comparison-summary-table .fee-value.diff-negative .savings-info {
    color: var(--ebc-positive);
}

.comparison-summary-table .fee-value.diff-positive .difference-percent {
    color: var(--ebc-danger);
}

/* Placeholder - BillVerification 스타일 재사용 */
.placeholder-section {
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-md);
    padding: 40px 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    box-shadow: var(--ebc-shadow-panel);
}

.placeholder-content {
    text-align: center;
    color: var(--ebc-text-muted);
}

.placeholder-content i {
    font-size: 28px;
    margin-bottom: 16px;
    opacity: 0.5;
}

.placeholder-content p {
    font-size: 14px;
    line-height: 1.6;
    margin: 0;
}

/* 계산 섹션 - BillVerification 스타일 재사용 */
.calc-section-compact {
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    padding: 10px 12px;
    margin-bottom: 10px;
}

.calc-section-compact:last-child {
    margin-bottom: 0;
}

.calc-section-header-compact {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: center;
    cursor: pointer;
}

.section-subtitle-compact {
    margin: 0;
    font-size: 12px;
    font-weight: 700;
    color: var(--ebc-text);
    padding: 0;
    border: none;
    display: flex;
    align-items: center;
    gap: 6px;
}

.recommended-badge-compact {
    background: var(--ebc-track);
    color: var(--ebc-text);
    padding: 4px 12px;
    border-radius: var(--ebc-radius-md);
    font-size: 11px;
    font-weight: 600;
}

.btn-toggle-compact {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: var(--ebc-btn-h-xs);
    padding: 0 8px;
    font-size: 11px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    background: white;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    color: var(--ebc-text-sub);
}

.btn-toggle-compact:hover {
    background: var(--ebc-surface-sunken);
    border-color: var(--ebc-primary);
    color: var(--ebc-primary);
}

.calc-card-compact {
    background: var(--ebc-surface-sunken);
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    padding: 10px;
    margin-top: 8px;
}

.calc-card-compact.recommendation-card-compact {
    background: var(--ebc-warn-tint);
    border-color: var(--ebc-warn);
    padding: 16px;
}

.recommendation-list-compact {
    margin-bottom: 16px;
}

.recommendation-list-compact:last-child {
    margin-bottom: 0;
}

.recommendation-item-compact {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 8px 0;
    font-size: 15px;
    color: var(--ebc-text);
    line-height: 1.6;
}

.recommendation-item-compact i {
    color: var(--ebc-warn);
    margin-top: 2px;
    flex-shrink: 0;
    font-size: 16px;
}

.recommendation-item-compact strong {
    font-weight: 700;
    font-size: 15px;
    color: var(--ebc-warn);
}

/* 상세 테이블 - BillVerification 스타일 재사용 */
.detail-table-compact {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
}

.detail-table-compact thead th {
    background: var(--ebc-surface-sunken);
    border: 1px solid var(--ebc-border);
    padding: 6px 8px;
    font-weight: 600;
    text-align: left;
    color: var(--ebc-text-sub);
    font-size: 12px;
}

.detail-table-compact thead th:first-child {
    width: 100px;
    min-width: 100px;
    max-width: 100px;
    word-break: keep-all;
    word-wrap: break-word;
    white-space: normal;
}

.detail-table-compact tbody td {
    border: 1px solid var(--ebc-border);
    padding: 6px 8px;
}

.detail-table-compact tbody td:first-child {
    word-break: keep-all;
    word-wrap: break-word;
    white-space: normal;
    line-height: 1.5;
}

.detail-table-compact tbody td.text-right {
    text-align: right;
}

.subtotal-row-compact {
    background: var(--ebc-surface-sunken);
    border-top: 2px solid var(--ebc-border);
}

.rate-plan-name-row {
    background: var(--ebc-track);
    border-bottom: 2px solid var(--ebc-border-strong);
}

.rate-plan-name-row td {
    padding: 10px 8px;
    font-weight: 600;
    color: var(--ebc-text);
}

/* 사용 패턴 */
.usage-pattern-compact {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 12px;
}

.pattern-item-compact {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.pattern-label-compact {
    font-size: 12px;
    font-weight: 600;
    color: var(--ebc-text-sub);
}

.pattern-bar-compact {
    height: 20px;
    background: var(--ebc-border);
    border-radius: var(--ebc-radius-sm);
    overflow: hidden;
    position: relative;
}

.pattern-bar-fill-compact {
    height: 100%;
    border-radius: var(--ebc-radius-sm);
    transition: width 0.3s ease;
}

.pattern-bar-fill-compact.low {
    background: var(--ebc-positive);
}

.pattern-bar-fill-compact.mid {
    background: var(--ebc-warn);
}

.pattern-bar-fill-compact.peak {
    background: var(--ebc-danger);
}

.pattern-value-compact {
    font-size: 12px;
    color: var(--ebc-text-sub);
    text-align: right;
}

.no-pattern-message {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 20px;
    color: var(--ebc-text-sub);
    font-size: 12px;
    text-align: center;
}

.no-pattern-message i {
    color: var(--ebc-primary);
    font-size: 16px;
}

/* 결과 그리드 */
.result-info-section-compact {
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--ebc-border);
}

.result-grid-compact {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
}

.result-item-compact {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px;
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
}

.result-item-compact.highlight-compact {
    background: var(--ebc-warn-tint);
    border-color: var(--ebc-warn);
}

.result-item-compact.total-compact {
    grid-column: span 2;
    background: var(--ebc-primary);
    color: white;
}

.result-item-compact.total-compact .result-label-compact,
.result-item-compact.total-compact .result-value-compact {
    color: white;
}

.result-label-compact {
    font-size: 12px;
    font-weight: 600;
    color: var(--ebc-text-sub);
}

.result-value-compact {
    font-size: 12px;
    font-weight: 700;
    color: var(--ebc-text);
}

/* 반응형 */
@media (max-width: 1024px) {
    .verification-layout {
        grid-template-columns: 1fr;
    }
}

/* 날짜 선택기 스타일 (년월만 선택) */
.date-picker-container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    padding: 0;
    z-index: 100;
    height: 38px;
    box-sizing: border-box;
}

.date-display {
    padding: 10px 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 120px;
    width: 100%;
    justify-content: center;
    border-radius: var(--ebc-radius-sm);
    transition: all 0.2s ease;
    color: var(--ebc-text);
    font-weight: 600;
    font-size: 13px;
    height: 100%;
    box-sizing: border-box;
}

.date-display:hover {
    background: var(--ebc-track);
}

.date-display i {
    font-size: 10px;
    transition: transform 0.2s ease;
}

.date-display i.rotate {
    transform: rotate(180deg);
}

.date-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-md);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    z-index: 101;
    margin-top: 6px;
    padding: 16px;
    min-width: 240px;
}

.year-selector {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid var(--ebc-border);
}

.year-selector button {
    background: var(--ebc-surface-sunken);
    border: 1px solid var(--ebc-border);
    padding: 4px 8px;
    cursor: pointer;
    color: var(--ebc-text-sub);
    border-radius: var(--ebc-radius-xs);
    transition: all 0.2s ease;
    font-weight: 600;
    font-size: 12px;
}

.year-selector button:focus {
    outline: none;
}

.year-selector button:hover {
    background: var(--ebc-primary);
    color: white;
    border-color: var(--ebc-primary);
}

.year-selector span {
    font-size: 14px;
    font-weight: 700;
    color: var(--ebc-text);
}

.month-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
}

.month-btn {
    background: var(--ebc-surface-sunken);
    border: 1px solid var(--ebc-border);
    padding: 8px 4px;
    cursor: pointer;
    border-radius: var(--ebc-radius-xs);
    font-size: 11px;
    font-weight: 600;
    color: var(--ebc-text-sub);
    transition: all 0.2s ease;
    text-align: center;
}

.month-btn:hover {
    background: var(--ebc-border);
    border-color: var(--ebc-border-strong);
    color: var(--ebc-text);
}

.month-btn.active {
    background: var(--ebc-primary);
    border-color: var(--ebc-primary);
    color: white;
    box-shadow: 0 1px 4px var(--ebc-primary-tint-strong);
}

.date-clear-btn {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--ebc-border);
}

.btn-clear-date {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    width: 100%;
    background: var(--ebc-surface-sunken);
    border: 1px solid var(--ebc-border);
    cursor: pointer;
    border-radius: var(--ebc-radius-xs);
    font-weight: 600;
    color: var(--ebc-text-sub);
    transition: all 0.2s ease;
}

.btn-clear-date:hover {
    background: var(--ebc-border);
    border-color: var(--ebc-border-strong);
    color: var(--ebc-text);
}

.info-display {
    padding: 8px 12px;
    background: var(--ebc-surface-sunken);
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    font-size: 13px;
    font-weight: 600;
    color: var(--ebc-text);
}
</style>
